// src/pages/Automations.tsx

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiFileText } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';
// import NewTopCards from './NewTopCards';
import AutomationsTable from './AutomationsTable';
import { getAutomations, formatDate, getTriggerDisplayName, mapStatusToDisplay } from './getAutomationsApi';
import { getAuthContext } from '../../utils/jwt';

const FiFileTextComponent = FiFileText as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

export default function Automations() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { savedAutomations } = useSelector((state: any) => state.automation);
  const [apiAutomations, setApiAutomations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resets automation data in the Redux store and navigates to the create automation page
  const handleSetupAutomation = () => {
    dispatch({ type: 'automation/resetAutomationData' });
    navigate('/automations/create');
  };

  // Fetch automations from API
  const fetchAutomations = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get auth context
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required. Please log in again.', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });
        setIsLoading(false);
        return;
      }

      // Call the API to get automations
      const response = await getAutomations(token, tenantId, userId);

      if (response && response.items) {
        // Transform API response to match the Automation interface
        const transformedAutomations = response.items.map((item: any) => ({
          id: item.id,
          name: item.name,
          trigger: getTriggerDisplayName(item.trigger?.type || ''),
          status: mapStatusToDisplay(item.status),
          createdAt: formatDate(item.createdAt),
          createdBy: item.createdBy || '-',
          updatedOn: formatDate(item.updatedAt),
          action: '',
          // Keep raw data for additional info if needed
          rawData: item,
        }));

        setApiAutomations(transformedAutomations);
      }
    } catch (error: any) {
      // console.error('Error fetching automations:', error);
      setError(error.message || 'Failed to load automations');
      toast.error(error.message || 'Failed to load automations. Please try again.', {
        position: 'top-right',
        autoClose: 4000,
        theme: 'colored',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch automations on component mount
  useEffect(() => {
    fetchAutomations();
  }, []);

  // Use API data if available, otherwise fallback to Redux data
  const displayAutomations = apiAutomations.length > 0 ? apiAutomations : savedAutomations.map((a: any) => ({
    id: a.id,
    name: a.name,
    trigger: a.trigger || '-',
    status: a.status === 'published' ? 'Active' : a.status === 'draft' ? 'Draft' : a.status,
    createdAt: a.timestamp || '-',
    createdBy: '-',
    updatedOn: '-',
    action: '',
  }));

  // Prepares data for the NewTopCards component
  // const topCardsData = {
  //   pausedAutomations: {
  //     value: displayAutomations.filter((a: any) => a.status === 'Draft' || a.status === 'draft').length,
  //   },
  //   activeAutomations: {
  //     value: displayAutomations.filter((a: any) => a.status === 'Active' || a.status === 'published').length,
  //   },
  //   totalUsersInFlows: { value: 'N/A', change: 'N/A' },
  //   overallConversionRate: { value: 'N/A', change: 'N/A' },
  // };

  return (
    <div className='p-2'>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      <div className='mb-6 flex justify-between items-center'>
        <div>
          <h2 className='text-[24px] font-bold text-[#3F5265]'>Automation</h2>
          <p className='text-[16px] text-[#657584]'>
            Our tools and expert support are designed to help you run smooth,
            effective campaigns without the stress.
          </p>
        </div>
        {displayAutomations.length > 0 && (
          <button
            onClick={handleSetupAutomation}
            className='bg-[#FF6B4A] hover:bg-[#ff5833] text-white px-4 py-2 rounded font-medium'
            style={{ fontSize: '14px' }}
          >
            + Create New Automation
          </button>
        )}
      </div>

      {isLoading ? (
        <div className='mt-6 flex flex-col items-center justify-center bg-white border border-gray-200 rounded-lg p-12 h-[500px]'>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B4A]"></div>
          </div>
          <p className='mt-4 text-gray-500'>Loading automations...</p>
        </div>
      ) : error ? (
        <div className='mt-6 flex flex-col items-center justify-center bg-white border border-gray-200 rounded-lg p-12 h-[500px]'>
          <div className='mb-4 text-red-500'>
            <FiFileTextComponent className='w-12 h-12' />
          </div>
          <h3 className='text-lg font-semibold text-gray-700 mb-2'>
            Failed to load automations
          </h3>
          <p className='text-center text-gray-500 max-w-md mb-6'>
            {error}
          </p>
          <button
            onClick={fetchAutomations}
            className='bg-[#FF6B4A] hover:bg-[#ff5833] text-white px-6 py-3 rounded-md font-medium'
          >
            Retry
          </button>
        </div>
      ) : displayAutomations.length === 0 ? (
        <div className='mt-6 flex flex-col items-center justify-center bg-white border border-gray-200 rounded-lg p-12 h-[500px]'>
          <div className='mb-4'>
            <FiFileTextComponent className='text-gray-400 w-12 h-12' />
          </div>
          <h3 className='text-lg font-semibold text-gray-700 mb-2'>
            No automations created yet
          </h3>
          <p className='text-center text-gray-500 max-w-md mb-6'>
            Start your first automation to share updates, news, or promotions
            with your audience. You can schedule, design, and track them all
            from here.
          </p>
          <button
            onClick={handleSetupAutomation}
            className='bg-[#FF6B4A] hover:bg-[#ff5833] text-white px-6 py-3 rounded-md font-medium'
          >
            + Setup Automation
          </button>
        </div>
      ) : (
        <>
          {/* <NewTopCards data={topCardsData} /> */}
          <AutomationsTable
            automations={displayAutomations}
            onRefresh={fetchAutomations}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  );
}